import React, { useEffect, useState } from 'react';
import { Colors, Text, View } from 'react-native-ui-lib';
import {
  useQuestionListener,
} from '../dataModel';
import {
  convertRouteParamsToInfo,
} from '../helperFunctions';
import { ContainerType, StanceType, ElementPath, FirestoreConstants } from '../typings/types';
import { FloatingActionButton } from './FloatingActionButton';
import { useIsDesktop, useIsMobile, useIsTablet } from '../MediaQueries';
import { strings } from '../localization/localization.web';
import { NothingHereInformation } from './Alert';
import { Footer } from './Footer';
import { ScrollView } from 'react-native-gesture-handler';
import { Dimensions } from 'react-native';
import { doc, DocumentData, getDoc, getFirestore } from 'firebase/firestore';
import { useUserState } from '../firebaseWrapper/firebaseWrapper';
import { StatementViewProps } from './StatementView';
import { QuestionView } from './QuestionView';

export function QuestionScreen({
  navigation,
  route,
}: StatementViewProps): JSX.Element {
  // QuestionScreen is only ever used as a Stack.Screen, so React Navigation only ever
  // supplies {navigation, route} as props -- location/statementId/groupId/memberId/
  // groupAdminLoggedIn must be derived from route.params instead (see ArgumentScreen,
  // which does the same for the equivalent group-detail screen).
  const groupId = route?.params?.groupId;
  const memberId = route?.params?.member;
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isDesktop = useIsDesktop();
  const [group, setGroup] = React.useState<DocumentData>();
  const [adminLoggedIn, setAdminLoggedIn] = React.useState(false);
  const [user, userLoading] = useUserState();
  const info: ElementPath = convertRouteParamsToInfo(route?.params) as ElementPath;
  const { question, loading, error } = useQuestionListener(info);

  useEffect(() => {
    if (groupId !== undefined) {
      const documentReference = doc(getFirestore(), FirestoreConstants.GROUPS, groupId);
      getDoc(documentReference).then((documentSnapshot) => {
        if (documentSnapshot.exists()) {
          setGroup({ ...documentSnapshot.data(), id: documentSnapshot.id });
        } else {
          console.log('No such document!');
        }
      }).catch((error) => {
        console.log('Error getting document:', error);
      });
    }
  }, []);

  useEffect(() => {
    if (group && user) {
      setAdminLoggedIn(group.admin === user.uid);
    }
  }, [group, user]);

  return (
    <View flex style={{ backgroundColor: Colors.primaryBG }}>
      <View style={{
        flex: 1,
        backgroundColor: Colors.secondaryBG,
        borderRadius: 15,
        marginHorizontal: isMobile ? 0 : 5,
        marginVertical: 5,
        overflow: 'hidden',
      }}>
        {isMobile || isTablet || isDesktop ? (
          <>
            <ScrollView showsVerticalScrollIndicator={false} style={{ margin: 10 }}>
              <QuestionView
                navigation={navigation}
                route={route}
                location={info.loc}
                statementId={info.id}
                groupId={groupId}
                groupAdminLoggedIn={adminLoggedIn}
                memberId={memberId}
              />
              {!loading && question.getChildData().length < 1 ?
                <ScrollView showsVerticalScrollIndicator={false}>
                  <NothingHereInformation text={strings.no_arguments_info} />
                  <View marginT-100 />
                  <Footer navigation={navigation} />
                </ScrollView>
                : question.getChildData().length < 3 ?
                  <>
                    <View style={{ height: Dimensions.get('window').height }} />
                    <Footer navigation={navigation} />
                  </>
                  :
                  <Footer navigation={navigation} />
              }
            </ScrollView>
          </>
        ) : (
          <>
            <ScrollView showsVerticalScrollIndicator={false} style={{ margin: 10 }}>
              {!loading
                && question.getChildData().filter(item => item[Object.keys(item)[0]] === StanceType.SUPPORT).length === 0
                && question.getChildData().filter(item => item[Object.keys(item)[0]] === StanceType.OPPOSE).length === 0 ?
                <>
                  <NothingHereInformation text={strings.no_arguments_info} />
                  <View marginT-100 />
                  <Footer navigation={navigation} />
                </>
                : question.getChildData().length < 3 ?
                  <>
                    <View style={{ height: Dimensions.get('window').height }} />
                    <Footer navigation={navigation} />
                  </>
                  :
                  <Footer navigation={navigation} />
              }
            </ScrollView>
          </>
        )}
      </View>
      <FloatingActionButton
        navigation={navigation}
        parentInfo={info}
        // A Question's children are answer Statements (optionally backed by a Source citation).
        possibleTypes={[ContainerType.STATEMENT, ContainerType.SOURCE]}
        groupId={'groupId' in info ? info.groupId : undefined}
        memberId={memberId}
        topicId={question.getTopics()[0]}
      />
    </View>
  );
}